import { z } from "zod";
import { smbOrderStatusSchema } from "./smb";

const text = z.string().nullable();
const bool = z.boolean().nullable();
const texts = z.array(z.string()).nullable();
export const smbClientIntakeLinkSchema = z.object({
  id: z.string(), label: text, submissionId: text,
  brand: z.object({ id: z.string(), name: z.string() }).strict().nullable(),
  status: z.enum(["active", "revoked", "expired"]), tokenLast4: z.string().length(4),
  createdAt: z.string(), updatedAt: z.string(), revokedAt: text,
  lastOpenedAt: text, expiresAt: text,
   recoverable: z.boolean().default(false),
}).strict();
export const smbClientIntakeRevisionSchema = z.object({
  number: z.number().int(), createdAt: z.string(), source: z.enum(["initial", "link_update"]),
}).strict();
const optionalText = (max: number) => z.string().trim().max(max).transform(v => v || null).nullish();
export const smbClientIntakeCreateLinkSchema = z.object({
  submissionId: z.string().uuid().optional(), brandId: z.string().uuid().optional(),
  label: optionalText(200), contactName: optionalText(200),
  contactEmail: z.union([z.literal(""), z.string().trim().email().max(254)]).transform(v => v || null).nullish(),
  contactPhone: optionalText(50), staffNote: optionalText(1000),
  expiresInDays: z.number().int().min(1).max(3650).optional(),
}).strict().refine(v => v.submissionId || v.brandId, "Choose an existing submission or permitted brand.");
export const smbClientIntakeIssuedLinkSchema = z.object({
  link: smbClientIntakeLinkSchema, url: z.string().url(), token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
}).strict();
export const smbClientIntakeRetrievedLinkSchema = z.object({
  url: z.string().url(), status: z.enum(["active", "revoked", "expired"]),
}).strict();
export const smbClientIntakeRestoreLinkSchema = z.object({
  originalUrl: z.string().min(1).max(2048),
}).strict();
export const smbClientIntakeRestoredLinkSchema = z.object({ ok: z.literal(true) }).strict();
export const smbClientIntakeBulkLinkSchema = z.object({
  submissionIds: z.array(z.string().uuid()).min(1).max(100),
}).strict();
export const smbClientIntakeBulkResultSchema = z.object({
  rows: z.array(z.discriminatedUnion("skippedReason", [
    z.object({ submissionId: z.string(), businessName: text, contactName: text, contactEmail: text,
      url: z.string().url(), token: z.string(), skippedReason: z.null() }).strict(),
    z.object({ submissionId: z.string(), skippedReason: z.enum(["not_found", "draft", "already_linked", "brand_unavailable"]) }).strict(),
  ])).max(100),
}).strict();
export type SmbClientIntakeLink = z.infer<typeof smbClientIntakeLinkSchema>;
export type SmbClientIntakeIssuedLink = z.infer<typeof smbClientIntakeIssuedLinkSchema>;
export type SmbClientIntakeBulkResult = z.infer<typeof smbClientIntakeBulkResultSchema>;
export const smbClientIntakeBrandLinksQuerySchema = z.object({
  brandId: z.string().uuid(), page: z.coerce.number().int().min(1).max(10000).default(1),
}).strict();
export const smbClientIntakeBrandLinksSchema = z.object({
  links: z.array(smbClientIntakeLinkSchema).max(25), total: z.number().int(),
  page: z.number().int(), pageSize: z.literal(25),
}).strict();
export const smbClientIntakeAdminQuerySchema = z.object({
  status: z.enum(["submitted", "draft", "all"]).catch("submitted").default("submitted"),
  q: z.string().max(200).catch("").default(""),
  page: z.preprocess(v => typeof v === "string" && /^[1-9]\d*$/.test(v) ? Number(v) : v,
    z.number().int().min(1).max(10000).catch(1)).default(1),
});
export const smbClientIntakeAdminAnswersSchema = z.object({
  business_name: text, contact_name: text, contact_email: text, contact_phone: text,
  website_url: text, no_website: bool, open_to_new_domain: bool, domain_no_preference: bool,
  domain_ending: text, domain_ending_other: text, existing_domain: text, industry: text, industry_other: text,
  tone: texts, tone_other: text, avoid_text: text, credentials_text: text, no_liked_sites: bool,
  address: text, service_radius: text, service_radius_other: text, cities_text: text, excluded_areas_text: text,
  icp_description: text, age_ranges: texts, pain_points: texts, why_choose_us: text, job_value: text,
  languages: text, languages_other: text, competitor_notes: text,
  facebook_url: text, instagram_url: text, tiktok_url: text, google_business_url: text, yelp_url: text, youtube_url: text,
  runs_ads: bool, ad_platforms: texts, ad_platforms_other: text, ad_monthly_spend: text, ad_what_worked: text,
  meta_account_status: text, meta_account_id: text, manage_account_for_me: bool, has_facebook_page: bool,
  tiktok_account_status: text, pixel_status: text, monthly_budget: text,
  goal_overall: text, goal_website: text, goal_static_ads: text, goal_video_ads: text, goal_more: text,
  main_goal: text, main_goal_other: text, monthly_lead_target: z.number().nullable(), busy_months: texts,
}).strict();
export const smbClientIntakeAdminRowSchema = z.object({
  id: z.string(), status: z.enum(["submitted", "draft"]), submitted_at: text, created_at: z.string(),
  business_name: text, contact_name: text, contact_email: text, website_url: text, no_website: bool,
  industry: text, industry_other: text, serviceCount: z.number().int(), logoCount: z.number().int(), photoCount: z.number().int(),
  brand_id: text, brand: z.object({ id: z.string(), name: z.string(), urlName: text }).strict().nullable(),
  hasLinkedOrder: z.boolean(),
  linkedOrderId: z.string().uuid().nullable().optional().default(null),
  linkedBrandId: z.string().nullable().optional().default(null),
  links: z.array(smbClientIntakeLinkSchema).default([]),
  revisionCount: z.number().int().default(0), lastRevisionAt: text.default(null),
  hasUnsubmittedEdits: z.boolean().default(false),
}).strict();
export const smbClientIntakeAdminListSchema = z.object({
  items: z.array(smbClientIntakeAdminRowSchema).max(25), total: z.number().int(), page: z.number().int(), pageSize: z.literal(25),
}).strict();
export const smbClientIntakeAdminAssetSchema = z.object({
  id: z.string(), kind: z.enum(["logo", "photo"]), filename: z.string(), contentType: z.string(),
  sizeBytes: z.number().int(), readUrl: text,
}).strict();
export const smbClientIntakeAdminDetailSchema = z.object({
  linkedOrderId: z.string().uuid().nullable().optional().default(null),
  linkedBrandId: z.string().nullable().optional().default(null),
  submission: smbClientIntakeAdminRowSchema,
  answers: smbClientIntakeAdminAnswersSchema,
  services: z.array(z.object({ name: text, description: text, starting_price: text, is_top_priority: bool }).strict()).max(25),
  promotions: z.array(z.object({ offer: text, dates: text, terms: text }).strict()).max(10),
  competitors: z.array(z.object({ name: text, url: text }).strict()).max(5),
  liked_sites: z.array(z.object({ url: text, note: text }).strict()).max(10),
  domain_ideas: z.array(z.object({ name: text }).strict()).max(3),
  brand_colors: z.array(z.object({ hex: text, label: text }).strict()).max(5),
  assets: z.array(smbClientIntakeAdminAssetSchema).max(45),
  revisions: z.array(smbClientIntakeRevisionSchema).default([]),
}).strict();
export const smbClientIntakeCreateOrderSchema = z.object({
  package: z.string().uuid().optional(),
  runProvision: z.boolean().default(false),
  // Older callers retain their existing all-assets conversion behavior.
  includePhotos: z.boolean().default(true),
}).strict();
export const smbClientIntakeCreateOrderResponseSchema = z.object({
  orderId: z.string().uuid(),
  brandId: z.string().nullable(),
  status: smbOrderStatusSchema,
  alreadyLinked: z.boolean(),
}).strict();
export type SmbClientIntakeCreateOrderResponse = z.infer<typeof smbClientIntakeCreateOrderResponseSchema>;
export type SmbClientIntakeAdminQuery = z.infer<typeof smbClientIntakeAdminQuerySchema>;
export type SmbClientIntakeAdminList = z.infer<typeof smbClientIntakeAdminListSchema>;
export type SmbClientIntakeAdminDetail = z.infer<typeof smbClientIntakeAdminDetailSchema>;

export function clientIntakeAdminHref(search: string, changes: Record<string, string | null>) {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === "") params.delete(key); else params.set(key, value);
  }
  return `/admin/smb?${params}`;
}
